/**
 * Maquina de estados de la UI del checkout (plan 7.3.3).
 *
 * `UiState` es la unica forma en que la carcasa decide que renderizar; el estado
 * canonico del negocio sigue siendo `payment_intents.status`. La traduccion no es
 * uno a uno: CREATED se abre en selector o flujo directo segun cuantos metodos pueda
 * RENDERIZAR esta build (ver `renderableMethodCount`), y CANCELED comparte pantalla con
 * FAILED (pero emite `apipay:cancel`, no `apipay:error`, decision que toma SessionPage
 * mirando el status crudo).
 */
import { KNOWN_GATEWAY_IDS } from '../gateways/GatewayFlow';
import type { CheckoutPaymentMethod, IntentStatus, PaymentIntent } from '../types/api';

export type UiState =
  | 'loading'
  | 'select_method'
  | 'gateway_flow'
  | 'requires_action'
  | 'processing'
  | 'success'
  | 'failed'
  | 'expired'
  | 'reversed'
  | 'blocked';

const TERMINAL_STATUSES: readonly IntentStatus[] = [
  'SUCCEEDED',
  'FAILED',
  'EXPIRED',
  'CANCELED',
  // Terminal aqui igual que en el dominio, aunque en el dominio SUCCEEDED tenga una salida hacia
  // el: lo que esta lista decide es si el widget deja de preguntar por el estado, y de REVERSED ya
  // no se sale.
  'REVERSED',
];

/** Estados sin salida en la matriz de transiciones de CONVENCIONES 2. */
export function isTerminalStatus(status: IntentStatus): boolean {
  return TERMINAL_STATUSES.includes(status);
}

/**
 * Las opciones de pago que esta build puede REALMENTE ofrecer para esta sesion.
 *
 * <p><strong>Es la unica definicion, y por eso vive aqui sola.</strong> La misma regla la necesitan
 * dos piezas —esta, para decidir si hay algo que elegir, y `MethodSelector`, para decidir que filas
 * pinta— y antes cada una la tenia escrita por su cuenta. Dos copias de la misma regla coinciden el
 * dia que se escriben y se separan en el primer cambio; lo que paga la separacion es el comprador:
 *
 * <ul>
 *   <li>si esta cuenta de mas, entra a un selector de UNA sola opcion, que es la pantalla de elegir
 *       sin nada que elegir;
 *   <li>si cuenta de menos, se va directo a un flujo que el selector habria descartado y recibe un
 *       `gateway_unavailable` seco en lugar del aviso que explica el caso.
 * </ul>
 *
 * <p>Con una sola funcion eso deja de ser un defecto que haya que vigilar en dos archivos.
 *
 * <p>Reglas, en este orden:
 *
 * <ol>
 *   <li>de `checkout.payment_methods` solo cuentan los `gateway_id` que estan en
 *       `KNOWN_GATEWAY_IDS`, las claves del mapa de estrategias. La API decide que pasarelas estan
 *       habilitadas para el tenant, pero habilitada no es lo mismo que renderizable aqui: una opcion
 *       visible que falla al pulsarla es peor que una opcion ausente. Un `gateway_id` vacio tampoco
 *       pertenece al conjunto, asi que este filtro subsume el `length > 0`;
 *   <li>una pasarela repetida cuenta UNA vez, y gana la primera. Dos filas con el mismo
 *       `gateway_id` no son dos opciones para quien elige: serian dos radios con el mismo valor y dos
 *       claves de React iguales, es decir el mismo medio de pago pintado dos veces;
 *   <li>sin bloque utilizable, la pasarela ACTIVA de la sesion es la unica candidata, y pasa por el
 *       mismo filtro que las demas: devolverla sin comprobarla seria colar por la puerta de atras
 *       precisamente lo que el filtro descarta.
 * </ol>
 */
export function renderableMethods(intent: PaymentIntent): CheckoutPaymentMethod[] {
  const offered: CheckoutPaymentMethod[] = [];
  const seen = new Set<string>();
  for (const method of intent.checkout.payment_methods ?? []) {
    if (!KNOWN_GATEWAY_IDS.has(method.gateway_id) || seen.has(method.gateway_id)) {
      continue;
    }
    seen.add(method.gateway_id);
    offered.push(method);
  }
  if (offered.length > 0) {
    return offered;
  }
  const active = intent.checkout.gateway_id;
  return KNOWN_GATEWAY_IDS.has(active) ? [{ gateway_id: active, public_config: {} }] : [];
}

/**
 * Cuantas opciones de pago puede REALMENTE ofrecer esta build para esta sesion.
 *
 * <p>De ahi la regla del mapeo: al flujo directo solo se va cuando hay EXACTAMENTE una opcion
 * renderizable. Cero tambien pasa por el selector, porque es el unico sitio que sabe explicarse.
 */
export function renderableMethodCount(intent: PaymentIntent): number {
  return renderableMethods(intent).length;
}

/** Mapeo canonico `payment_intents.status` -> `UiState`. */
export function mapIntentToUiState(intent: PaymentIntent): UiState {
  switch (intent.status) {
    case 'CREATED':
      // Con UNA sola opcion renderizable se salta el selector y se va directo al flujo.
      return renderableMethodCount(intent) === 1 ? 'gateway_flow' : 'select_method';
    case 'REQUIRES_ACTION':
      return 'requires_action';
    case 'PROCESSING':
      return 'processing';
    /*
     * El pago de una empresa espera firmas en el banco (Santander 007/008). Se queda en el flujo de
     * la pasarela, que es quien sabe explicarlo (`SantanderPopupFlow` pinta «pendiente de
     * autorizacion»). No es `processing`: esa pantalla promete un desenlace en segundos y este
     * puede tardar horas.
     */
    case 'PENDING_AUTHORIZATION':
      return 'requires_action';
    case 'SUCCEEDED':
      return 'success';
    case 'FAILED':
      return 'failed';
    case 'EXPIRED':
      return 'expired';
    case 'CANCELED':
      return 'failed';
    /*
     * LA VENTA SE COBRO Y EL COMERCIO LA ANULO DESPUES. Pantalla propia, y las dos alternativas
     * baratas se descartaron por mentir:
     *
     *   - `success` diria «pago realizado» sobre un cobro que ya no vale;
     *   - `failed` diria «no pudimos procesar tu pago» sobre un pago que SI se proceso, e invitaria
     *     a reintentar algo que el API responderia con un 409.
     *
     * Quien llega aqui es el comprador que reabre un enlace viejo, y lo que necesita saber es que
     * el cargo se deshizo y con que referencia preguntar.
     */
    case 'REVERSED':
      return 'reversed';
  }
}
